import {
  SlideOverDrawer,
  DrawerHeader,
  DrawerBody,
} from '@/components/ui/slide-over-drawer';
import { StatusBadge, type StatusType } from '@/components/ui/status-badge';
import { Spinner } from '@/components/ui/spinner';
import { usePortOrderStore } from '../stores/port-order-store';
import { usePortOrder } from '../hooks';
import { PortTimeline } from './port-timeline';
import { formatPhoneNumber } from '@/lib/utils';
import { Phone, Calendar, Building2, Users, MessageSquare } from 'lucide-react';
import type { PortOrderStatus } from '../types';

// Parse port notes from Python repr format
interface PortNote {
  author: string;
  postDate: string;
  note: string;
}

function parsePortNotes(notesStr: string | null): PortNote[] {
  if (!notesStr) return [];

  try {
    // The notes come as a Python repr string like:
    // {'PortNote': [{'author': 'sarah', 'postDate': datetime.datetime(...), 'note': '...'}]}
    // We need to extract the relevant parts using regex

    const notes: PortNote[] = [];

    // Match each note block
    const notePattern = /\{\s*'author':\s*'([^']*)',\s*'postDate':\s*datetime\.datetime\((\d+),\s*(\d+),\s*(\d+),\s*(\d+),\s*(\d+),\s*(\d+)(?:,\s*\d+)?\),\s*'note':\s*'((?:[^'\\]|\\.)*)'/g;

    let match;
    while ((match = notePattern.exec(notesStr)) !== null) {
      const author = match[1] || 'Unknown';
      const year = match[2] || '2024';
      const month = match[3] || '1';
      const day = match[4] || '1';
      const hour = match[5] || '0';
      const minute = match[6] || '0';
      const second = match[7] || '0';
      const noteText = match[8] || '';

      // Construct date
      const date = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hour),
        parseInt(minute),
        parseInt(second)
      );

      // Unescape the note text
      const unescapedNote = noteText
        .replace(/\\n/g, '\n')
        .replace(/\\'/g, "'")
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');

      notes.push({
        author,
        postDate: date.toISOString(),
        note: unescapedNote,
      });
    }

    return notes;
  } catch {
    // If parsing fails, return empty array
    return [];
  }
}

// Format note date
function formatNoteDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

// Notes Timeline Component
interface NotesTimelineProps {
  notes: string | null;
}

function NotesTimeline({ notes }: NotesTimelineProps) {
  const parsedNotes = parsePortNotes(notes);

  if (parsedNotes.length === 0) {
    // Show raw notes as fallback if parsing failed but notes exist
    if (notes) {
      return (
        <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-100 whitespace-pre-wrap">
          {notes}
        </p>
      );
    }
    return null;
  }

  return (
    <div className="space-y-4">
      {parsedNotes.map((note, index) => (
        <div key={index} className="relative pl-6 pb-4 last:pb-0">
          {/* Timeline line */}
          {index < parsedNotes.length - 1 && (
            <div className="absolute left-[9px] top-6 bottom-0 w-px bg-slate-200" />
          )}

          {/* Timeline dot */}
          <div className="absolute left-0 top-1 w-[18px] h-[18px] rounded-full bg-indigo-100 border-2 border-indigo-400 flex items-center justify-center">
            <MessageSquare size={10} className="text-indigo-600" />
          </div>

          {/* Note content */}
          <div className="bg-slate-50 rounded-lg border border-slate-100 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-900 capitalize">
                {note.author}
              </span>
              <span className="text-xs text-slate-400">
                {formatNoteDate(note.postDate)}
              </span>
            </div>
            <p className="text-sm text-slate-600 whitespace-pre-wrap">
              {note.note}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

// Map port order status to StatusBadge status type
function getStatusType(status: PortOrderStatus | null): StatusType {
  switch (status) {
    case 'COMPLETED':
      return 'completed';
    case 'REJECTED':
      return 'rejected';
    case 'CANCELLED':
      return 'inactive';
    case 'FOCd':
    case 'SUBMITTED':
    case 'PENDING':
    case 'NEW':
      return 'pending';
    default:
      return 'pending';
  }
}

// Get status label
function getStatusLabel(status: PortOrderStatus | null): string {
  switch (status) {
    case 'COMPLETED':
      return 'Completed';
    case 'REJECTED':
      return 'Rejected';
    case 'CANCELLED':
      return 'Cancelled';
    case 'FOCd':
      return 'FOC Received';
    case 'SUBMITTED':
      return 'In Progress';
    case 'PENDING':
      return 'Pending';
    case 'NEW':
      return 'New';
    default:
      return 'Unknown';
  }
}

// Format date for display
function formatDate(dateString: string | null): string {
  if (!dateString) return 'TBD';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

// Team member avatar component
interface TeamAvatarProps {
  name: string;
  className?: string;
}

function TeamAvatar({ name, className }: TeamAvatarProps) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      className={`w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-medium ${className || ''}`}
      title={name}
    >
      {initials}
    </div>
  );
}

export function PortOrderDrawer() {
  const { selectedPortId, isDrawerOpen, closeDrawer } = usePortOrderStore();
  const { data: portOrder, isLoading } = usePortOrder(selectedPortId);

  if (!selectedPortId) return null;

  return (
    <SlideOverDrawer isOpen={isDrawerOpen} onClose={closeDrawer} width="xl">
      {/* Dark Header */}
      <DrawerHeader
        title={`#PORT-${String(selectedPortId).padStart(5, '0')}`}
        subtitle={portOrder?.porting_from || 'Loading...'}
        onClose={closeDrawer}
        badge={
          <StatusBadge
            status={getStatusType(portOrder?.status || null)}
            label={getStatusLabel(portOrder?.status || null)}
          />
        }
      />

      {/* Content */}
      <DrawerBody className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : portOrder ? (
          <div className="space-y-6 animate-fade-in">
            {/* Quick Info Cards */}
            <div className="grid grid-cols-2 gap-4">
              {/* Losing Carrier */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <Building2 size={14} />
                  <span>Losing Carrier</span>
                </div>
                <p className="font-medium text-slate-900">
                  {portOrder.porting_from || 'Unknown'}
                </p>
              </div>

              {/* Est. Completion */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <Calendar size={14} />
                  <span>Est. Completion</span>
                </div>
                <p className="font-medium text-slate-900">
                  {formatDate(portOrder.foc_date)}
                </p>
              </div>
            </div>

            {/* Team Section */}
            {portOrder.sales_rep && (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
                  <Users size={14} />
                  <span>Team</span>
                </div>
                <div className="flex items-center gap-2">
                  <TeamAvatar name={portOrder.sales_rep} />
                  <span className="text-sm text-slate-700">{portOrder.sales_rep}</span>
                </div>
              </div>
            )}

            {/* Timeline Section */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-wide">
                Port Progress
              </h3>
              <PortTimeline
                status={portOrder.status}
                submittedDate={portOrder.submitted_date}
                focDate={portOrder.foc_date}
                completedDate={
                  portOrder.status === 'COMPLETED' ? portOrder.foc_date : null
                }
              />
            </div>

            {/* DIDs Being Ported */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wide flex items-center gap-2">
                <Phone size={14} />
                Numbers in This Order ({portOrder.did_count})
              </h3>
              <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                <div className="divide-y divide-slate-100">
                  {portOrder.dids.map((tn) => (
                    <div
                      key={tn}
                      className="px-4 py-2.5 text-sm font-mono text-indigo-600 hover:bg-slate-50"
                    >
                      {formatPhoneNumber(tn)}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Notes Section */}
            {portOrder.notes && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wide flex items-center gap-2">
                  <MessageSquare size={14} />
                  Notes
                </h3>
                <NotesTimeline notes={portOrder.notes} />
              </div>
            )}

            {/* Additional Details */}
            <div className="text-xs text-slate-400 border-t border-slate-100 pt-4">
              <div className="flex justify-between">
                <span>Order Reference: {portOrder.cust_order_ref || 'N/A'}</span>
                <span>Submitted: {formatDate(portOrder.submitted_date)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-500">
            Port order not found
          </div>
        )}
      </DrawerBody>
    </SlideOverDrawer>
  );
}
